import styles from './page-layout.module.scss';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Container } from '../components/atoms/Base/Container/Container';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import Home from '../components/pages/Home/Home';
import SignInButton from '../components/atoms/Custom/SignInButton/sign-in-button';
import AuthenticatedContainer from '../components/molecules/AuthenticatedContainer/AuthenticatedContainer';
import { StringHelper } from '../common/helpers/string-helper';
import CollapseButton from '../components/atoms/Custom/CollapseButton/CollapseButton';
import { useClassNameBuilderFactory } from '../common/providers/ReactKitProvider';
import { useScreenDetection } from 'common/helpers/screen-detection';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthApiService } from '../api/providers/AppServiceProvider';
import { PagePermissionDto } from '../models/auth/pagepermissions';
import { Page } from '../common/enums/page-enums';
import { PrimaryRoutes } from '../common/enums/internal-route-enums';
import { NavigationItem, UserData } from 'common/models/navigation';
import { Sidebar } from 'components/atoms/Base/Layout/Sidebar';
import { Topbar } from 'components/atoms/Base/Layout/Topbar';
import AIConversation from 'components/organisms/ai-chat/ai-conversation';
import { getUserFromAccount } from 'common/helpers/auth-helper';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';
import FeatureExplainerModal from 'components/feature-explainer/feature-explainer-modal';
import AiHelperModal, { AiHelperChatMessage } from 'components/feature-explainer/ai-helper-modal';
import { captureAppMainScreenshot, APP_MAIN_CONTENT_ID } from 'components/feature-explainer/capture-app-main-screenshot';
import { useTrackClick } from 'hooks/useTrackClick';

interface IPageLayoutProps {
	children?: ReactNode;
	isLoading: boolean;
	permittedPages?: string[];
	isLoadingPermissions?: boolean;
	hasPermissionError?: boolean;
}

const PageLayout: FC<IPageLayoutProps> = ({
	children,
	isLoading,
	permittedPages = [],
	isLoadingPermissions = false,
	hasPermissionError = false,
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { accounts } = useMsal();
	const { screenInfo } = useScreenDetection();

	const [leftOpen, setLeftOpen] = React.useState(false);
	const [rightOpen, setRightOpen] = React.useState(false);

	const classNamesForLogoContainer = useClassNameBuilderFactory()
		.Create()
		.AddClass(styles.logo_container)
		.AddConditionalClass(accounts.length === 0, styles.not_signed_in)
		.GetClassNames();

	const [isCollapsed, setIsCollapsed] = useState<boolean>(screenInfo.isMobile);
	const [currentLocation, setCurrentLocation] = useState<string>(StringHelper.Empty);

	const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
	const [activeNavItem, setActiveNavItem] = useState('dashboard');
	const aiChatApi = useAiChatApiService();
	const trackClick = useTrackClick();
	const [explainerOpen, setExplainerOpen] = useState(false);
	const [explainerLoading, setExplainerLoading] = useState(false);
	const [explainerText, setExplainerText] = useState('');
	const [explainerError, setExplainerError] = useState<string | null>(null);
	const [explainerBusy, setExplainerBusy] = useState(false);
	const [aiHelperOpen, setAiHelperOpen] = useState(false);
	const [aiHelperLoading, setAiHelperLoading] = useState(false);
	const [aiHelperError, setAiHelperError] = useState<string | null>(null);
	const [aiHelperMessages, setAiHelperMessages] = useState<AiHelperChatMessage[]>([]);
	const canUseAnalystSidebar = permittedPages.includes(Page.AIAnalyst);
	const canUseFinderSidebar = permittedPages.includes(Page.Finder);
	const canUseExplainer = permittedPages.includes(Page.Explainer);
	const analystRouteActive =
		location.pathname === PrimaryRoutes.AIAnalyst ||
		location.pathname.startsWith(`${PrimaryRoutes.AIAnalyst}/`);

	// Get user data from Azure B2C account
	const user: UserData = getUserFromAccount(accounts[0]) || {
		id: 'unknown',
		name: 'Unknown User',
		email: '',
	};

	const handleSidebarToggle = () => {
		setSidebarCollapsed(!sidebarCollapsed);
	};

	const closeExplainerModal = useCallback(() => {
		trackClick('Explainer Closed', 'explainer-modal-close');
		setExplainerOpen(false);
	}, [trackClick]);

	const handleFeatureExplainer = useCallback(async () => {
		setExplainerBusy(true);
		setExplainerError(null);
		setExplainerText('');
		try {
			const shot = await captureAppMainScreenshot();
			setExplainerOpen(true);
			trackClick('Explainer Opened', 'explainer-modal-open');
			setExplainerLoading(true);
			try {
				const res = await aiChatApi.explainUiScreenshot(shot);
				setExplainerText(res.explanation || '');
				trackClick('Explainer Completed', 'explainer-completed');
			} catch (apiErr) {
				setExplainerError(apiErr instanceof Error ? apiErr.message : 'Request failed');
				trackClick('Explainer Failed', 'explainer-api-failed');
			} finally {
				setExplainerLoading(false);
			}
		} catch (e) {
			setExplainerOpen(true);
			trackClick('Explainer Opened', 'explainer-modal-open');
			setExplainerLoading(false);
			setExplainerError(e instanceof Error ? e.message : 'Could not capture the page');
			trackClick('Explainer Failed', 'explainer-capture-failed');
		} finally {
			setExplainerBusy(false);
		}
	}, [aiChatApi, trackClick]);

	const handleAiHelperOpen = useCallback(() => {
		setAiHelperError(null);
		setAiHelperOpen(true);
		trackClick('Finder Opened', 'finder-modal-open');
	}, [trackClick]);

	const handleAiHelperClose = useCallback(() => {
		trackClick('Finder Closed', 'finder-modal-close');
		setAiHelperOpen(false);
	}, [trackClick]);

	const handleAnalystNavigate = useCallback(() => {
		navigate(PrimaryRoutes.AIAnalyst);
		setActiveNavItem('analyst');
		setLeftOpen(false);
	}, [navigate]);

	const handleAiHelperSendMessage = useCallback(
		async (text: string) => {
			//trackClick('Finder Message Sent', 'finder-message-sent');
			setAiHelperError(null);
			const userMessage: AiHelperChatMessage = {
				id: `${Date.now()}-user`,
				role: 'user',
				content: text,
			};

			const nextMessages = [...aiHelperMessages, userMessage];
			setAiHelperMessages(nextMessages);
			setAiHelperLoading(true);

			try {
				const res = await aiChatApi.askAiHelper({
					question: text,
					chat_history: nextMessages.map((m) => ({ role: m.role, content: m.content })),
				});
				const assistantMessage: AiHelperChatMessage = {
					id: `${Date.now()}-assistant`,
					role: 'assistant',
					content: res.answer || 'No answer returned.',
				};
				setAiHelperMessages((prev) => [...prev, assistantMessage]);
			} catch (err) {
				setAiHelperError(err instanceof Error ? err.message : 'Request failed');
				trackClick('Finder Failed', 'finder-message-failed');
			} finally {
				setAiHelperLoading(false);
			}
		},
		[aiChatApi, aiHelperMessages, trackClick]
	);

	const handleNavigation = (item: NavigationItem) => {
		setActiveNavItem(item.id);
		console.log('Navigating to:', item.routes);
	};

	const onToggleMenuHandler = () => {
		setIsCollapsed(!isCollapsed);
	};

	// Compute left container classes based on collapsed state
	const leftContainerClassNames = isCollapsed
		? `${styles.left_container} ${styles.collapsed}`
		: styles.left_container;

	// Handle location changes
	useEffect(() => {
		if (StringHelper.IsNullOrEmpty(currentLocation)) {
			setCurrentLocation(location.pathname);
		} else if (location.pathname !== currentLocation) {
			setCurrentLocation(location.pathname);
			setIsCollapsed(true);
			// Don't depend on leftContainerClassNames here - just set the collapsed state
			// The left container classes will be updated by the other useEffect
		}
	}, [location.pathname, currentLocation]);

	return (
		<Container>
			<UnauthenticatedTemplate>
				<Container className={leftContainerClassNames}>
					<Container className={classNamesForLogoContainer} horizontal centerFull bottomSpace>
						{/* <Logo /> */}
					</Container>
					<Container className={styles.authentication} centerFull>
						<SignInButton isLoading={isLoading} />
					</Container>
				</Container>
				<Container className={styles.right_container}>
					<Container center topSpace>
						{screenInfo.isMobile && <SignInButton isLoading={isLoading} />}
					</Container>
				</Container>
			</UnauthenticatedTemplate>

			<AuthenticatedTemplate>
				{isLoadingPermissions ? (
					<Container fullWidth centerFull className="min-h-screen">
						<div className="text-center">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
							<p className="text-ui text-gray-600">Loading permissions...</p>
						</div>
					</Container>
				) : hasPermissionError ? (
					<Container fullWidth centerFull className="min-h-screen">
						<div className="text-center max-w-md mx-auto p-8">
							<div className="mb-6">
								<svg
									className="mx-auto h-16 w-16 text-red-400"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.25}
										d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<h1 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load Data</h1>
							<p className="text-gray-600 mb-4">Please refresh the page to try again.</p>
						</div>
					</Container>
				) : (
					<>
						<div className="h-screen flex overflow-hidden bg-gray-50 border-r border-tertiary-background">
							{/* Sidebar - visible on desktop */}
							<div className="hidden md:flex">
								<Sidebar
									collapsed={sidebarCollapsed}
									onToggle={handleSidebarToggle}
									activeItem={activeNavItem}
									onNavigate={handleNavigation}
									permittedPages={permittedPages}
									user={user}
									onAnalyst={canUseAnalystSidebar ? handleAnalystNavigate : undefined}
									analystActive={analystRouteActive}
									onAiHelper={canUseFinderSidebar ? handleAiHelperOpen : undefined}
									finderActive={aiHelperOpen}
									aiHelperBusy={aiHelperLoading}
									onFeatureExplainer={canUseExplainer ? handleFeatureExplainer : undefined}
									explainerActive={explainerOpen}
									featureExplainerBusy={explainerBusy}
								/>
							</div>

							{/* Mobile drawer sidebar (opens when Topbar left button clicked) */}
							{leftOpen && (
							<div className="fixed inset-0 z-[60] flex md:hidden">
								{/* Sidebar drawer */}
								<div className="w-64 bg-white shadow-lg">
								<Sidebar
									collapsed={false}
									insideDrawer
									onToggle={() => setLeftOpen(false)}
									activeItem={activeNavItem}
									onNavigate={handleNavigation}
									permittedPages={permittedPages}
									user={user}
									onAnalyst={canUseAnalystSidebar ? handleAnalystNavigate : undefined}
									analystActive={analystRouteActive}
									onAiHelper={canUseFinderSidebar ? handleAiHelperOpen : undefined}
									finderActive={aiHelperOpen}
									aiHelperBusy={aiHelperLoading}
									onFeatureExplainer={canUseExplainer ? handleFeatureExplainer : undefined}
									explainerActive={explainerOpen}
									featureExplainerBusy={explainerBusy}
								/>
								</div>

								{/* Overlay */}
								<div
								className="flex-1 bg-black/40"
								onClick={() => setLeftOpen(false)}
								/>
							</div>
							)}

							{rightOpen && (
							<div className="fixed inset-0 z-[60] flex md:hidden">
								{/* Overlay to close - positioned first */}
								<div className="flex-1 bg-black/40" onClick={() => setRightOpen(false)}></div>

								{/* Right panel - positioned last to appear on right */}
								<div className="w-60 bg-[#F5F8FA] shadow-lg h-full">
								<div className="p-4">
									<AIConversation onSidebarCollapsedChange={(collapsed) => {
									if (collapsed) {
										setRightOpen(false); 
									}
									}} />
								</div>
								</div>
							</div>
							)}

							{/* Main area */}
							<div className="flex-1 flex flex-col overflow-hidden">
								{/* Topbar - mobile only */}
								<div className="md:hidden">
									<Topbar
										onOpenLeft={() => setLeftOpen(true)}
										onOpenRight={() => setRightOpen(true)}
									/>
								</div>

								{/* Page content */}
								<main
									id={APP_MAIN_CONTENT_ID}
									className={
										[PrimaryRoutes.GrowthModel, PrimaryRoutes.JourneyExplorer].some(
											(route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
										)
											? 'flex-1 min-h-0 overflow-hidden bg-white'
											: 'flex-1 overflow-auto bg-white'
									}
								>
									{children}
								</main>
							</div>
						</div>

						<FeatureExplainerModal
							open={explainerOpen}
							onClose={closeExplainerModal}
							loading={explainerLoading}
							explanation={explainerText}
							error={explainerError}
						/>
						<AiHelperModal
							open={aiHelperOpen}
							onClose={handleAiHelperClose}
							messages={aiHelperMessages}
							loading={aiHelperLoading}
							error={aiHelperError}
							onSendMessage={handleAiHelperSendMessage}
						/>

						{/* <Container className={leftContainerClassNames}>
							<Container>
								<Container className={classNamesForLogoContainer} horizontal centerFull bottomSpace>
									<Container>
										<Logo />
									</Container>
									<Container>
										<CollapseButton isCollapsed={isCollapsed} onToggle={onToggleMenuHandler} />
									</Container>
								</Container>
								<Container className={styles.navigation}>
									<NavigationMenu permittedPages={permittedPages} />
								</Container>
								<Container className={styles.authentication} centerFull>
									<AuthenticatedContainer isLoading={isLoading} />
								</Container>
							</Container>
						</Container>
						 */}
					</>
				)}
			</AuthenticatedTemplate>
		</Container>
	);
};

export default PageLayout;
